import dayjs from 'dayjs';
import React, { ReactElement } from 'react';
import ReactMarkdown from 'react-markdown';
import styled from 'styled-components';

import BreadCrumbs from '@/components/atom/BreadCrumbs';
import MaterialIcon from '@/components/atom/MaterialIcon';
import TagList from '@/components/atom/TagList';
import ClickLogger from '@/components/common/ClickLogger';
import ContentBottom from '@/components/common/communication/ContentBottom';
import ContentRelatedQna from '@/components/common/communication/ContentRelatedQna';
import LinkTag from '@/components/common/link-tag';
import MarkdownToc from '@/components/common/markdown-toc';
import TemplateLayout from '@/components/layouts/TemplateLayout';
import StickyAppHeader from '@/components/molecules/sticky-app-header';
import FaqStickyButton from '@/components/templates/communication/faq/faq-detail-template/faq-sticky-button';
import {
  Background,
  Bottom,
  BreadCrumbsWrapper,
  Content,
  Cta,
  Divider,
  FootNote,
  MagazineTagsWrapper,
  MarkDown,
  NavigatorWrapper,
  NextNavigator,
  PrevNavigator,
  TagWrapper,
  Wrapper,
} from '@/components/templates/communication/magazine/magazine-detail-template';
import MagazineDetailTags from '@/components/templates/communication/magazine/magazine-detail-template/magazine-detail-tags';
import RelatedMagazineList from '@/components/templates/communication/magazine/magazine-detail-template/related-magazine-list';
import AppInstallTopBanner from '@/components/templates/mobile/communication/common/AppInstallTopBanner';
import MagazineDetailSkeleton from '@/components/templates/mobile/communication/magazine/MagazineDetailSkeleton';
import { DeeplinkLoggerEventName } from '@/constants/logs/deeplink';
import { MAGAZINE_LOG_EVENT } from '@/constants/logs/magazine';
import useAppInstallTopBanner from '@/hooks/cuj/useAppInstallTopBanner';
import { FaqDetailViewModel } from '@/hooks/faq/useFaqDetailViewModel';
import { AdBandCta } from '@/shared/ui/';
import ContentMarkDown from '@/shared/ui/content-mark-down/ContentMarkDown';
import theme from '@/styles/theme';
import { ClientType, CustomPageProps } from '@/types/app';
import { ROUTES } from '@/types/common';
import { absoluteTime } from '@/utils/format';

interface Props extends CustomPageProps {
  viewModel: FaqDetailViewModel;
  topBannerViewModel: ReturnType<typeof useAppInstallTopBanner>;
}

const FaqDetailTemplate = ({ viewModel, topBannerViewModel, client }: Props) => {
  if (!viewModel.faq) {
    return <MagazineDetailSkeleton />;
  }

  return (
    <TemplateLayout.Wrapper>
      {client.isWebView && (
        <TemplateLayout.Header useSafeArea>
          <StickyAppHeader title='1분 FAQ' />
        </TemplateLayout.Header>
      )}
      {client.clientType === ClientType.MOBILE_WEB && topBannerViewModel.isVisible && (
        <AdBandCta
          client={client}
          onClose={topBannerViewModel.hideBanner}
          enableAnimation={true}
          fallbackContent={
            <AppInstallTopBanner
              onClickClose={topBannerViewModel.hideBanner}
              onLogEvent={viewModel.onClickTopCta}
              content='web_content_bnr_service'
              trackingInfo={{
                campaign: 'web_content_faq',
                contentId: viewModel.faq.magazinePid,
                contentTitle: viewModel.faq.title,
                contentCategory: viewModel.diseaseTags[0]?.name ?? '',
                contentSubCategory: viewModel.departmentTags[0]?.name ?? '',
              }}
            />
          }
        />
      )}
      <Background>
        <TemplateLayout.Content>
          {client.clientType === ClientType.WEB && (
            <BreadCrumbsWrapper>
              <BreadCrumbs
                visible
                name={viewModel.faq.title}
                path={`${ROUTES.FAQ.DETAIL.path}/${viewModel.faq.title}`}
                urlParameter={viewModel.faq.magazinePid}
                type='magazine'
              />
            </BreadCrumbsWrapper>
          )}
          <Wrapper>
            <Header>
              <hgroup>
                <h1>{viewModel.title}</h1>
                <h2>{viewModel.description}</h2>
                <time dateTime={absoluteTime(viewModel.faq.createdAt ?? dayjs().date())}>
                  {dayjs(viewModel.faq.createdAt).format('YYYY.MM.DD')}
                </time>
              </hgroup>
            </Header>
            <Content>
              <MarkDown>
                <ContentMarkDown
                  markdown={viewModel.faqContent}
                  components={{
                    h2: (node, ...rest) => {
                      return (
                        <h2 id={`${node.children}`} {...rest}>
                          {node.children}
                        </h2>
                      );
                    },
                    h3: (node, ...rest) => {
                      return (
                        <h3 id={`${node.children}`} {...rest}>
                          {node.children}
                        </h3>
                      );
                    },
                    h4: (node, ...rest) => {
                      return (
                        <h4 id={`${node.children}`} {...rest}>
                          {node.children}
                        </h4>
                      );
                    },
                    div: (node, ...rest) => {
                      if (node.className === 'toc') {
                        return (
                          <div id='toc' {...rest}>
                            <MarkdownToc
                              content={viewModel.faqContent}
                              magazineTocLogger={viewModel.magazineTocLogger}
                            />
                          </div>
                        );
                      }
                      if (node.className === 'cta5' || node.className === 'cta4') {
                        const cta5 = node.children[1] as ReactElement<Cta, 'button'>;
                        const { name, value, children } = cta5.props;

                        return (
                          <div className={node.className} {...rest}>
                            <button name={name} onClick={() => viewModel.moveToHospital(value)}>
                              {children}
                            </button>
                          </div>
                        );
                      }

                      return (
                        <div className={node.className} {...rest}>
                          {node.children}
                        </div>
                      );
                    },
                    a: (node) => {
                      if (node.href?.includes('https://abr.ge')) {
                        return (
                          <ClickLogger
                            params={{
                              eventName: DeeplinkLoggerEventName.ClickCtaButton,
                              payload: {
                                magazinePid: viewModel.faq?.magazinePid ?? '',
                                linkUrl: node.href ?? '',
                                ab_term: viewModel.extractTextFromChildren(node.children),
                                ab_campaign: 'web_content_faq',
                                ab_content: 'web_content_btn_details',
                                ab_sub_id: viewModel.airbridgeLinkOptions.sub_id ?? '',
                                ab_sub_id_1: viewModel.airbridgeLinkOptions.sub_id_1 ?? '',
                                ab_sub_id_2: viewModel.airbridgeLinkOptions.sub_id_2 ?? '',
                                ab_sub_id_3: viewModel.airbridgeLinkOptions.sub_id_3 ?? '',
                              },
                            }}
                          >
                            <LinkTag
                              href={viewModel.getAirbridgeDynamicLink({
                                ...viewModel.airbridgeLinkOptions,
                                campaign: 'web_content_faq',
                                content: 'web_content_btn_details',
                                term: viewModel.extractTextFromChildren(node.children),
                              })}
                              onClickNavigateToScreen={() =>
                                viewModel.onClickOuterLinkWithLinkText(
                                  viewModel.getAirbridgeDynamicLink({
                                    ...viewModel.airbridgeLinkOptions,
                                    campaign: 'web_content_faq',
                                    content: 'web_content_btn_details',
                                    term: viewModel.extractTextFromChildren(node.children),
                                  })
                                )
                              }
                              target='_blank'
                              className='markdown-link'
                              wrapperStyle={{
                                display: 'inline-block',
                              }}
                            >
                              {node.children}
                            </LinkTag>
                          </ClickLogger>
                        );
                      }
                      return (
                        <ClickLogger
                          params={{
                            eventName: MAGAZINE_LOG_EVENT.CLICK_OUTER_LINK,
                            payload: {
                              magazinePid: viewModel.faq?.magazinePid ?? '',
                              linkUrl: node.href ?? '',
                              linkText: viewModel.extractTextFromChildren(node.children),
                            },
                          }}
                        >
                          <LinkTag
                            href={node.href ?? ''}
                            onClickNavigateToScreen={() => viewModel.onClickOuterLinkWithLinkText(node.href ?? '')}
                            target='_blank'
                            className='markdown-link'
                            wrapperStyle={{
                              display: 'inline-block',
                            }}
                          >
                            {node.children}
                          </LinkTag>
                        </ClickLogger>
                      );
                    },
                  }}
                />
              </MarkDown>
              <MagazineTagsWrapper>
                {viewModel.diseaseTags.length > 0 && (
                  <MagazineDetailTags
                    title='관련 질병 알아보기'
                    magazinePid={viewModel.faq.magazinePid ?? ''}
                    tags={viewModel.diseaseTags}
                    onClick={viewModel.moveToTotalSearch}
                  />
                )}
                {viewModel.symptomTags.length > 0 && (
                  <MagazineDetailTags
                    title='관련 증상 알아보기'
                    magazinePid={viewModel.faq.magazinePid ?? ''}
                    tags={viewModel.symptomTags}
                    onClick={viewModel.moveToTotalSearch}
                  />
                )}
                {viewModel.departmentTags.length > 0 && (
                  <MagazineDetailTags
                    title='관련 진료 병원 찾기'
                    magazinePid={viewModel.faq.magazinePid ?? ''}
                    tags={viewModel.departmentTags}
                    onClick={viewModel.moveToHospital}
                  />
                )}
              </MagazineTagsWrapper>
              <FootNote>
                {/* eslint-disable-next-line react/no-children-prop */}
                <ReactMarkdown children={viewModel.faq.footnote ?? ''} />
              </FootNote>
              <TagWrapper>
                <TagList
                  tags={viewModel.faqTagNames.map((tag) => tag)}
                  padding={[8, 16]}
                  onClickTagName={viewModel.onClickTag}
                />
              </TagWrapper>
              <Bottom isLiked={viewModel.faq.isLikedByMe}>
                <ContentBottom
                  onClickLike={viewModel.onClickLike}
                  isLikedByMe={viewModel.faq.isLikedByMe}
                  likeCount={viewModel.faq.likeCount}
                  copyUrl={viewModel.copyUrl}
                  onClickLog={viewModel.onClickShare}
                  id={viewModel.faq.magazinePid ?? ''}
                  title={viewModel.faq.title ?? ''}
                  description={viewModel.faq.description ?? ''}
                  titleImageUrl={`${process.env.NEXT_PUBLIC_MAGAZINE_OG_IMAGE}`}
                  onRecordLog={viewModel.onRecordLog}
                />
              </Bottom>

              <NavigatorWrapper>
                {viewModel.prevFaqPid && (
                  <LinkTag
                    href={`${ROUTES.FAQ.DETAIL.path}/${viewModel.prevFaqPid}`}
                    onClickNavigateToScreen={() => viewModel.onClickPrevNextFaq(viewModel.prevFaqPid ?? '')}
                  >
                    <PrevNavigator>
                      <MaterialIcon name={'arrow_back'} size={16} color={theme.color.gray600} />
                      이전 글
                    </PrevNavigator>
                  </LinkTag>
                )}
                {viewModel.nextFaqPid && (
                  <LinkTag
                    href={`${ROUTES.FAQ.DETAIL.path}/${viewModel.nextFaqPid}`}
                    onClickNavigateToScreen={() => viewModel.onClickPrevNextFaq(viewModel.nextFaqPid ?? '')}
                  >
                    <NextNavigator>
                      다음 글
                      <MaterialIcon name={'arrow_forward'} size={16} color={theme.color.gray600} />
                    </NextNavigator>
                  </LinkTag>
                )}
              </NavigatorWrapper>
            </Content>

            {viewModel.relatedFaq && viewModel.relatedFaq.length > 0 && (
              <RelatedMagazineList
                totalRelatedMagazines={viewModel.relatedFaq}
                totalRelatedMagazinesTitle={'이런 콘텐츠는 어떠세요?'}
                moveOtherMagazine={viewModel.moveOtherMagazine}
              />
            )}
            {viewModel.faqRelatedQna && viewModel.faqRelatedQna.length > 0 && (
              <>
                <Divider />
                <ContentRelatedQna
                  relatedQna={viewModel.faqRelatedQna}
                  onClickRelatedQna={viewModel.onClickRelatedQna}
                />
              </>
            )}
          </Wrapper>
          <FaqStickyButton
            faq={viewModel.faq}
            bottomButtonText={viewModel.bottomButtonText}
            shouldShowEventText={viewModel.shouldShowEventText()}
            onClickButton={viewModel.handleStickyButton}
          />
        </TemplateLayout.Content>
      </Background>
    </TemplateLayout.Wrapper>
  );
};

export default FaqDetailTemplate;

const Header = styled.header`
  width: 100%;
  padding: 8px 24px 32px 24px;

  hgroup {
    display: flex;
    flex-direction: column;
    word-break: keep-all;
    width: 100%;
    padding-right: 24px;
    white-space: pre-wrap;
  }

  h1 {
    ${({ theme }) => theme.typography.UI26SB};
    line-height: 36px;
    color: ${({ theme }) => theme.color.gray800};
    margin-bottom: 8px;
    word-break: keep-all;
  }

  h2 {
    width: fit-content;
    word-break: keep-all;
    ${({ theme }) => theme.typography.UI18M};
    color: ${({ theme }) => theme.color.gray600};
    margin-bottom: 8px;
  }

  time {
    ${({ theme }) => theme.typography.UI16R};
    color: ${({ theme }) => theme.color.gray300};

    svg {
      margin: 0 8px;
    }
  }
`;
